import { Box, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import * as XLSX from "xlsx";
import {
  blackColor,
  blackShade19,
  greenShade13,
  greenShade14,
  greenShade15,
  greyColor060,
  greyColor56,
  perpleShade1,
  perpleShade3,
  tamrindColor01,
  whiteColor,
} from "../../configs/styles/muiThemes";
import {
  addThousandsSeparator,
  getLocationInfo,
  getOrderDeliveryTypeText,
  getOrderTypeTextWithCancelType,
} from "../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { getStatsCountInfoData } from "../../reducers/businessDashboard/BusinessDashboardSlice";
import ReportTile from "./ReportTile";
import moment from "moment";
import { downloadOrderSaleReportsApi } from "../../service/api_calls/ordersReports/OrderReportsAPi";
import { CURRENT_DATE } from "../../service/constant";
import {
  getOrderReportByCityData,
  getOrderReportByTypeYearData,
  getSaleAmtDataByYear,
  getTableSaleAmtDataByMonth,
  getTableSaleAmtDataByMonthPreviousYear,
} from "../../reducers/orderSummary/orderSummarySlice";
import { toTitleCase } from "../../utils/validationUtils";
import {
  getBuyerAppLookupAPI,
  getMainProductsWithVariantsAPI,
} from "../../service/api_calls/BusinessPreview/BusinessPreviewAPI";

function ReportDownloadContent() {
  const [salesData, setSalesData] = useState(null);
  const [saleAnalysisError, setSaleAnalysisError] = useState("");
  const [saleExcelLoading, setSaleExcelLoading] = useState(false);

  const [orderSummaryDate, setOrderSummaryDate] = useState({
    from: "2022-01-01",
    to: moment().format("YYYY-MM-DD"),
  });
  const [orderSummaryError, setOrderSummaryError] = useState("");

  const [excelLoading, setExcelLoading] = useState(false);
  const [excelErr, setExcelErr] = useState("");

  const [bapNameList, setBapNameList] = useState(null);

  const currentYear = moment().year();
  const today = moment();
  const monthOffset = today.date() > 4 ? 1 : 2;
  const adjustedMonth = moment().subtract(monthOffset, "months").format("MM");

  const [gstData, setGstData] = useState({
    month: adjustedMonth,
    year: currentYear,
  });
  const [gstAnalysisError, setGstAnalysisError] = useState("");
  const [gstExcelLoading, setGstExcelLoading] = useState(false);

  const dispatch = useDispatch();

  const { statsCountInfo } = useSelector((state) => state.businessDashboard);
  const {
    orderSaleAmtTableMonthData,
    orderSaleAmtTableMonthPreviousYearData,
    orderSaleAmtTableMonthDataLoader,
    orderSaleAmtYearData,
    orderSaleAmtYearDataLoader,
    orderReportByCity,
  } = useSelector((state) => state.orderSummary);

  const { activeBusinessInfo } = useSelector((state) => state.businessPreview);

  const getBapDisplayNameInfo = async () => {
    const response = await getBuyerAppLookupAPI();

    if (response?.details?.length > 0) {
      setBapNameList(response?.details || []);
    }
  };

  // Get ONDC Bap Display Name by Subscriber ID
  const getOndcBapNameBySubscriberId = (id) => {
    const data = bapNameList?.find((item) => item?.subscriberId === id);
    return data ? data?.displayName : "";
  };

  function calculateTax(amt, taxPercentage) {
    const taxAmount = (amt * taxPercentage) / 100;
    return taxAmount;
  }

  useEffect(() => {
    const businessId = sessionStorage.getItem("selectedBusinessId");

    getBapDisplayNameInfo();

    if (!statsCountInfo) {
      dispatch(getStatsCountInfoData(businessId));
    }
  }, []);

  const onSalesDownload = async () => {
    if (!salesData?.from) {
      setSaleAnalysisError("Enter the Start date");
      return;
    }
    if (!salesData?.to) {
      setSaleAnalysisError("Enter the End date");
      return;
    }

    const fromDate = moment(salesData?.from).startOf("day");
    const toDate = moment(salesData?.to).endOf("day");
    const isFromBeforToDate = fromDate?.isBefore(toDate);

    if (!isFromBeforToDate) {
      setSaleAnalysisError("To date must be after the from date.");
      return;
    }

    const diffInMs = toDate - fromDate;
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInDays > 31) {
      setSaleAnalysisError("Date range cannot exceed 31 days");
      return;
    }

    setSaleAnalysisError("");

    setSaleExcelLoading(true);

    const businessId = sessionStorage.getItem("selectedBusinessId");

    const downloadJsonRes = await downloadOrderSaleReportsApi({
      businessId,
      fromDate: moment(salesData?.from).startOf("day").valueOf(),
      toDate: moment(salesData?.to).endOf("day").valueOf(),
    });

    const fileName = `Order_Detail_Analysis_${moment().format("Do_MM_YYYY")}`;
    const businessName = activeBusinessInfo?.name || "";

    const headers = [
      [`Order Details Analysis`],
      [
        `${businessName}`,
        `Report Date - ${moment().format("Do MMM YYYY")}`,
        `Report Date Range - ${moment(salesData?.from).format(
          "Do MMM YYYY"
        )} to ${moment(salesData?.to).format("Do MMM YYYY")}`,
      ],
      [
        `Total Orders - ${downloadJsonRes?.details?.orderCount}`,
        `Total Sales - ₹ ${downloadJsonRes?.details?.totalOrderSales}`,
      ],
      [],
      [
        "S.no",
        "Order No",
        "Order Type",
        "ONDC Order No",
        "Buyer App",
        "Order Date",
        "Order Status",
        "Delivery Method",
        "Delivery Date",
        "Customer Name",
        "Address",
        "City",
        "State",
        "Zip",
        "Contact No",
        "Order Qty",
        "Product Total",
        "Shipping Cost (Paid by Customer)",
        "Discount",
        "Order Total",
        "Estimated Shipping Cost (To be charged to business)",
        "CostBo Incentives",
        "Product Name",
        "Weight (in gm)",
        "Quantity Ordered",
        "Actual Price",
        "Sale Price",
        "Sale Price after Incentive",
        "Tax Rate",
        "Tax Amount",
        "Incentive",
        "Incentive Code",
      ],
    ];

    try {
      // Process orders and products
      const formattedData = downloadJsonRes?.details?.data?.flatMap(
        (item, index) => {
          const incentive = item.productList.reduce((sum, product) => {
            const discounts = product.productDiscounts || [];
            const productDiscountSum = discounts.reduce(
              (dSum, d) => dSum + d.discount,
              0
            );
            return sum + productDiscountSum;
          }, 0);
          const orderDetails = [
            index + 1,
            item?.orderId || "",
            item?.ondcCart ? "ONDC Order" : "Website Order",
            item?.ondcInfo?.bapOrderId || "",
            item?.ondcInfo?.bapId
              ? getOndcBapNameBySubscriberId(item?.ondcInfo?.bapId)
              : "-",
            item?.orderCreatedOn
              ? moment(item?.orderCreatedOn).format("Do MMM, YYYY hh:mm A")
              : "",
            item?.orderStatus
              ? getOrderTypeTextWithCancelType(item?.orderStatus)
              : "",
            item?.deliveryType?.type
              ? getOrderDeliveryTypeText(item?.deliveryType?.type)
              : "",

            item?.orderDeliveredOn
              ? moment(item?.orderDeliveredOn).format("Do MMM, YYYY hh:mm A")
              : "-",
            item?.customerAddress?.name || "",
            item?.customerAddress?.addressLine1 || "",
            item?.customerAddress?.city || "",
            item?.customerAddress?.state || "",
            item?.customerAddress?.zip || "",
            item?.customerAddress?.phoneNo || "",

            item?.totalQuantity || "",
            item?.orderTotalProductCost || "",
            item?.shippingCost ?? "",
            item?.claimedPoint > 0 ? item?.claimedPoint : "",
            item?.orderTotalCost || "",
            item?.discountDetails?.[0]?.discountType == "immediateDiscount"
              ? item?.discountDetails?.[0]?.discountAmount
              : "",
            incentive || "",
          ];

          return item?.productList?.map((product, productIndex) => {
            return [
              ...(productIndex === 0
                ? orderDetails
                : new Array(orderDetails.length).fill("")),
              product?.productName || "",
              product?.measurementInfo?.weight || "",
              product?.currentQuantity || "",
              product?.productOriginalPrice * product?.currentQuantity || "",
              product?.productTotalBeforeDiscount || "",
              product?.productTotalPrice || "",
              product?.productTaxRate || "",
              calculateTax(product?.productTotalPrice, product?.productTaxRate),
              product?.productDiscounts?.[0]?.discount || "",
              product?.productDiscounts?.[0]?.code || "",
            ];
          });
        }
      );

      // Combine headers and data
      const finalData = [...headers, ...formattedData];

      // Convert to worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(finalData);

      // Create workbook and append worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

      // Write workbook and create Blob
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const excelBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download link
      const url = URL.createObjectURL(excelBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.xlsx`;
      document.body.appendChild(a);

      setTimeout(() => {
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setSaleExcelLoading(false);
      }, 2000);
    } catch (error) {
      console.error("Error exporting data:", error);
      setSaleExcelLoading(false);
    }
  };

  const onGstDownload = async () => {
    if (!gstData?.month || !gstData?.year) {
      setGstAnalysisError("Enter the Year and month");
      return;
    }

    // Create moment objects using the first day of each month for comparison
    const current = moment();

    // Create end of month date for "to"
    const date = moment(`${gstData?.year}-${gstData?.month}-01`);

    // Check if from date is before to date
    const isFromBeforeCurrentDate = date.isBefore(current);

    if (!isFromBeforeCurrentDate) {
      setGstAnalysisError("Date cannot be in future.");
      return;
    }

    // Clear any previous errors
    setGstAnalysisError("");

    setGstExcelLoading(true);

    const businessId = sessionStorage.getItem("selectedBusinessId");

    const downloadJsonRes = await downloadOrderSaleReportsApi({
      businessId,
      fromDate: moment(`${gstData?.year}-${gstData?.month}-01`)
        .startOf("day")
        .valueOf(),
      toDate: moment(`${gstData?.year}-${gstData?.month}-01`)
        .endOf("month")
        .valueOf(),
      orderStatus: "orderdelivered,",
    });

    const response = await getMainProductsWithVariantsAPI(businessId);

    const productList = response?.details || [];

    console.log("productListproductList", productList);

    const fileName = `Order_GST_Analysis_${moment().format("Do_MM_YYYY")}`;
    const businessName = activeBusinessInfo?.name || "";

    console.log("activeBusinessInfo", activeBusinessInfo);

    const headers = [
      [`Order GST Analysis`],
      [
        `${businessName}`,
        `Report Date - ${moment().format("Do MMM YYYY")}`,
        `Report Of ${moment(`${gstData?.year}-${gstData?.month}-01`).format(
          "MMMM YYYY"
        )}`,
      ],
      [`GST Number: ${activeBusinessInfo?.gstnumber}`],
      // [`Total Orders - `, `Total Sales - ₹ `],
      [],
      [
        "S.no",
        "Seller Gstin",
        "Order No",
        "ONDC Order No",
        "Order Date",
        "Delivery Date",
        "Shipment Id",
        "Bill From City",
        "Bill From State",
        "Bill From Country",
        "Bill From Postal Code",
        "Ship From City",
        "Ship From State",
        "Ship From Country",
        "Ship From Postal Code",
        "Ship To City",
        "Ship To State",
        "Ship To Country",
        "Ship To Postal Code",
        "Order Qty",
        "Product Total",
        "Order Total",
        "Product Name",
        "Product Code",
        "Sku Id",
        "Weight (in gm)",
        "Quantity Ordered",
        "Sale Price",
        "Tax Exclusive Gross",
        "Tax Rate",
        "Tax Amount",
        "CGST Rate",
        "SGST Rate",
        "Igst Rate",
        "CGST Amount",
        "SGST Amount",
        "Igst Amount",
      ],
    ];

    try {
      // Process orders and products
      const formattedData = downloadJsonRes?.details?.data?.flatMap(
        (item, index) => {
          const orderDetails = [
            index + 1,
            activeBusinessInfo?.gstnumber || "",
            item?.orderId || "",
            item?.ondcInfo?.bapOrderId || "",
            // item?.shipmentInfo?.carrierTrackId || "",
            item?.orderCreatedOn
              ? moment(item?.orderCreatedOn).format("Do MMM, YYYY hh:mm A")
              : "",

            item?.orderDeliveredOn
              ? moment(item?.orderDeliveredOn).format("Do MMM, YYYY hh:mm A")
              : "-",
            item?.shipmentInfo?.carrierTrackId || "",

            getLocationInfo(activeBusinessInfo?.businessArea, "city"),

            getLocationInfo(
              activeBusinessInfo?.businessArea,
              "state"
            )?.toUpperCase(),
            "IN",
            getLocationInfo(activeBusinessInfo?.businessArea, "pincode"),

            getLocationInfo(activeBusinessInfo?.businessArea, "city"),

            getLocationInfo(
              activeBusinessInfo?.businessArea,
              "state"
            )?.toUpperCase(),
            "IN",
            getLocationInfo(activeBusinessInfo?.businessArea, "pincode"),

            item?.customerAddress?.city || "",
            item?.customerAddress?.state
              ? item?.customerAddress?.state?.toUpperCase()
              : "",
            "IN",
            item?.customerAddress?.zip || "",

            item?.totalQuantity || "",
            item?.orderTotalProductCost || "",
            item?.orderTotalCost || "",
          ];

          return item?.productList?.map((product, productIndex) => {
            const matchProduct = productList?.find(
              (item) => item?.id === product?.productId
            );
            console.log("product", product);
            return [
              ...orderDetails,
              // ...(productIndex === 0
              //   ? orderDetails
              //   : new Array(orderDetails.length).fill("")),
              product?.productName || "",
              `${matchProduct?.ondc?.productCode?.key}:${matchProduct?.ondc?.productCode?.value}`,
              matchProduct?.skuId || "",
              product?.measurementInfo?.weight || "",
              product?.currentQuantity || "",
              product?.productSalePrice * product?.currentQuantity || "",
              Number(product?.productSalePrice * product?.currentQuantity) -
                Number(product?.productTax) || "",
              product?.productTaxRate || "",
              product?.productTax?.toFixed(2),
              getLocationInfo(activeBusinessInfo?.businessArea, "state") ===
              item?.customerAddress?.state
                ? Number(product?.productTaxRate) / 2
                : 0,
              getLocationInfo(activeBusinessInfo?.businessArea, "state") ===
              item?.customerAddress?.state
                ? Number(product?.productTaxRate) / 2
                : 0,
              getLocationInfo(activeBusinessInfo?.businessArea, "state") !==
              item?.customerAddress?.state
                ? Number(product?.productTaxRate)
                : 0,
              getLocationInfo(activeBusinessInfo?.businessArea, "state") ===
              item?.customerAddress?.state
                ? Number(product?.productTax) / 2
                : 0,
              getLocationInfo(activeBusinessInfo?.businessArea, "state") ===
              item?.customerAddress?.state
                ? Number(product?.productTax) / 2
                : 0,
              getLocationInfo(activeBusinessInfo?.businessArea, "state") !==
              item?.customerAddress?.state
                ? Number(product?.productTax)
                : 0,
            ];
          });
        }
      );

      // Combine headers and data
      const finalData = [...headers, ...formattedData];

      // Convert to worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(finalData);

      const headerRange = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let C = headerRange.s.c; C <= headerRange.e.c; C++) {
        for (let R = 0; R <= 4; R++) {
          // First 5 rows (headers)
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!worksheet[cellAddress]) continue;

          worksheet[cellAddress].s = {
            fill: { fgColor: { rgb: "FFFF00" } }, // Yellow background
            font: { bold: true, color: { rgb: "000000" } }, // Black text
            alignment: { horizontal: "center", vertical: "center" },
          };
        }
      }

      // Create workbook and append worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

      // Write workbook and create Blob
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const excelBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download link
      const url = URL.createObjectURL(excelBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.xlsx`;
      document.body.appendChild(a);

      setTimeout(() => {
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setGstExcelLoading(false);
      }, 2000);
    } catch (error) {
      console.error("Error exporting data:", error);
      setGstExcelLoading(false);
    }
  };

  const onOrderSummaryDownload = async () => {
    console.log("dates", orderSummaryDate?.from, orderSummaryDate?.to);

    if (!orderSummaryDate?.from) {
      setOrderSummaryError("Enter the Start date");
      return;
    }
    if (!orderSummaryDate?.to) {
      setOrderSummaryError("Enter the End date");
      return;
    }

    const fromDate = moment(orderSummaryDate?.from);
    const toDate = moment(orderSummaryDate?.to);
    const isFromBeforToDate = fromDate?.isBefore(toDate);

    if (!isFromBeforToDate) {
      setOrderSummaryError("To date must be after the from date.");
      return;
    }

    setOrderSummaryError("");

    const businessId = sessionStorage.getItem("selectedBusinessId");

    // const downloadJsonRes = downloadOrderSaleReportsApi({
    //   businessId,
    //   fromDate: moment(orderSummaryDate?.from).format("YYYY-MM-DD"),
    //   toDate: moment(orderSummaryDate?.to).format("YYYY-MM-DD"),
    // });
    const currentYear = moment().year();
    dispatch(getOrderReportByCityData(businessId));
    dispatch(
      getTableSaleAmtDataByMonth({
        businessId,
        params: {
          from: moment(`${currentYear}-01-01`).startOf("day").valueOf(),
          to: moment(`${currentYear}-12-31`).endOf("day").valueOf(),
          groupBy: "month",
          pageNo: "1",
          pageSize: "1",
        },
        type: "year",
      })
    );
    dispatch(
      getTableSaleAmtDataByMonthPreviousYear({
        businessId,
        params: {
          from: moment(`${currentYear}-01-01`)
            .subtract(1, "year")
            .startOf("day")
            .valueOf(),
          to: moment(`${currentYear}-12-31`)
            .subtract(1, "year")
            .endOf("day")
            .valueOf(),
          groupBy: "month",
          pageNo: "1",
          pageSize: "1",
        },
        type: "year",
      })
    );
    dispatch(
      getSaleAmtDataByYear({
        businessId,
        params: {
          from: moment(`2022-01-01`).startOf("day").valueOf(),
          to: moment(`${currentYear}-12-31`).endOf("day").valueOf(),
          groupBy: "year",
          pageNo: "1",
          pageSize: "1",
        },
      })
    );

    exportToExcel();
  };

  const exportToExcel = async () => {
    console.log("check123", orderReportByCity);

    const businessId = sessionStorage.getItem("selectedBusinessId");
    const currentYear = moment().year();

    setExcelLoading(true);

    const fileName = `Sales_Summary_Report_${moment().format("Do_MM_YYYY")}`;
    const businessName = activeBusinessInfo?.name || "";

    const headers = [
      ["Sales Summary Report"],
      [
        `${businessName}`,
        `Report Date - ${moment().format("Do MMM YYYY")}`,
        // `Order reports From-${orderSummaryDate?.from}`,
        // `Order reports To-${orderSummaryDate?.to}`,
      ],
      [
        `Sales reports From-${orderSummaryDate?.from}`,
        `Sales reports To-${orderSummaryDate?.to}`,
      ],
      [
        `Total Orders - ${
          statsCountInfo?.orderCountInfo?.details?.totalOrders || 0
        }`,
        `Total Sales - ₹ ${addThousandsSeparator(
          statsCountInfo?.orderCountInfo?.details?.totalAmount
        )}`,
        `Last Order Date - ${
          statsCountInfo?.orderCountInfo?.details?.recentOrderSummary?.length >
          0
            ? moment(
                statsCountInfo?.orderCountInfo?.details?.recentOrderSummary[0]
                  ?.orderDate
              ).format("Do MMM YYYY")
            : "N/A"
        }`,
      ],
      [],
      ["Order & Sales By Year"],
      ["Year", "Orders", "Sales"],
    ];

    try {
      const ordersSummaryByYears = orderSaleAmtYearData?.map((item) => [
        item?.timePeriod,
        item?.orderCount,
        `₹ ${item?.totalSales}`,
      ]);

      const ordersSummaryByMonths = [
        ["Month", ...orderSaleAmtTableMonthData.map((item) => item?.month)],
        [
          "Orders",
          ...orderSaleAmtTableMonthData.map((item) => item?.orderCount),
        ],
        [
          "Sales",
          ...orderSaleAmtTableMonthData.map((item) => `${item?.totalSales}`),
        ],
      ];
      const ordersSummaryByMonthsPreviousYear = [
        [
          "Month",
          ...orderSaleAmtTableMonthPreviousYearData.map((item) => item?.month),
        ],
        [
          "Orders",
          ...orderSaleAmtTableMonthPreviousYearData.map(
            (item) => item?.orderCount
          ),
        ],
        [
          "Sales",
          ...orderSaleAmtTableMonthPreviousYearData.map(
            (item) => `${item?.totalSales}`
          ),
        ],
      ];

      const orderSummaryReportByCity = orderReportByCity
        ?.slice(0, 20)
        .map((item, index) => [
          index + 1,
          toTitleCase(item?.cityState?.city),
          toTitleCase(item?.cityState?.state),
          item?.orderCount,
        ]);

      const finalData = [
        ...headers,
        ...ordersSummaryByYears,
        [],
        [`Order & Sales By Month for ${currentYear}`],
        ...ordersSummaryByMonths,
        [],
        [`Order & Sales By Month for ${currentYear - 1}`],
        ...ordersSummaryByMonthsPreviousYear,
        [],
        ["Top 20 Sales by Area", "", "", ""],
        ["S.No", "City", "State", "Orders"],
        ...orderSummaryReportByCity,
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(finalData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const excelBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download link
      const url = URL.createObjectURL(excelBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.xlsx`;
      document.body.appendChild(a);

      setTimeout(() => {
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setExcelLoading(false);
      }, 2000);
    } catch (error) {
      console.error("Error exporting data:", error);
      setExcelLoading(false);
    }
  };

  return (
    <Stack
      sx={{
        background: whiteColor,
        borderRadius: "12px",
        padding: "15px",
        mt: "20px",
      }}
      gap="20px"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize={17} fontWeight={600} color={blackShade19}>
          Download Reports
        </Typography>

        {statsCountInfo?.orderCountInfo?.details?.totalOrders > 0 && (
          <Box display={"flex"} gap={2}>
            <TotalOrderDetail
              label={"Total Orders"}
              value={statsCountInfo?.orderCountInfo?.details?.totalOrders || 0}
              borderColor={`1px solid ${perpleShade3}`}
              bgColor={perpleShade1}
              icon={<Inventory2OutlinedIcon sx={{ fontSize: "26px", pr: 1 }} />}
              iconColor={tamrindColor01}
            />

            <TotalOrderDetail
              label={"Total Sales"}
              value={`₹ ${addThousandsSeparator(
                statsCountInfo?.orderCountInfo?.details?.totalAmount
              )}`}
              borderColor={`1px solid ${greenShade14}`}
              bgColor={greenShade13}
              icon={
                <AccountBalanceWalletOutlinedIcon
                  sx={{ fontSize: "26px", pr: 1 }}
                />
              }
              iconColor={greenShade15}
            />
          </Box>
        )}
      </Box>

      <Stack sx={{ border: "1px solid " + greyColor56, borderRadius: "12px" }}>
        <ReportTile
          title="Sales Analysis Report"
          infoText="Max date range of 31 Days"
          data={salesData}
          setData={setSalesData}
          onDownload={onSalesDownload}
          error={saleAnalysisError}
          loader={saleExcelLoading}
        />
        <ReportTile
          title="GST Report"
          bottomInfoText="GST report for previous month will be available after 4th of current month."
          data={gstData}
          setData={setGstData}
          monthYearPicker
          onDownload={onGstDownload}
          error={gstAnalysisError}
          loader={gstExcelLoading}
        />
        {/* <ReportTile title="Settlement Report" /> */}
        <ReportTile
          title="Order Summary Report"
          subTitle={`Order Summary Report As of `}
          data={orderSummaryDate}
          setData={setOrderSummaryDate}
          onDownload={onOrderSummaryDownload}
          error={orderSummaryError}
          loader={excelLoading}
          hideBorder
          customFont={13}
        />
      </Stack>
    </Stack>
  );
}

export default ReportDownloadContent;

function TotalOrderDetail({
  label,
  value,
  hideLabel,
  borderColor,
  bgColor,
  icon,
  iconColor,
}) {
  return (
    <Box
      sx={{
        background: bgColor,
        border: borderColor,
        width: "230px",
        height: "55px",
        display: "flex",
        alignItems: "center",
        marginTop: "1px",
        padding: "8px 10px",
        borderRadius: "10px",
      }}
    >
      <Box
        sx={{
          width: "35px",
          height: "35px",
          position: "relative",
          top: "0px",
          left: "-10",
          color: "#ffffff",
          background: iconColor,
          border: "1px solid #fff",
          borderRadius: "8px",
          lineHeight: "27px",
          marginRight: "12px",
          padding: "3px 8px",
          textAlign: "center",
        }}
      >
        {icon}
      </Box>
      <Box>
        {!hideLabel ? (
          <Typography
            variant="h3"
            fontSize={{ xs: 12, md: 12 }}
            color={greyColor060}
            fontWeight={400}
            lineHeight={1.5}
            mt={1}
            // sx={{ ...styles }}
          >
            {label}{" "}
          </Typography>
        ) : (
          <Box sx={{ height: "22px" }} />
        )}

        <Typography
          variant="body1"
          fontSize={{ xs: 12.5, md: 14 }}
          color={blackColor}
          fontWeight={600}
          minHeight={{ md: "28px", xs: "24px" }}
        >
          {value}
        </Typography>
      </Box>
      {/* <Box
        component="img"
        maxHeight={{ xs: "29px", md: "40px" }}
        src={}
        alt="CostBo"
      /> */}
    </Box>
  );
}
